import { Injectable } from '@angular/core';

export type CookieConsentState = 'accepted' | 'declined' | null;

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly storageKey = 'cookieConsent';
  private readonly matomoUrl = 'https://matomo.fotobox-leipzig.de/';
  private readonly matomoSiteId = '4';
  private readonly debugEnabled = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  getConsent(): CookieConsentState {
    const value = localStorage.getItem(this.storageKey);
    if (value === 'accepted' || value === 'declined') {
      return value;
    }
    return null;
  }

  hasAnalyticsConsent(): boolean {
    return this.getConsent() === 'accepted';
  }

  acceptAnalytics(): void {
    localStorage.setItem(this.storageKey, 'accepted');
    if (this.debugEnabled) {
      console.info('[Matomo] Consent accepted');
    }
    this.initMatomo();
  }

  declineAnalytics(): void {
    localStorage.setItem(this.storageKey, 'declined');
    if (this.debugEnabled) {
      console.info('[Matomo] Consent declined');
    }
  }

  initMatomo(): void {
    const w = window as any;
    if (w._paq) {
      if (this.debugEnabled) {
        console.info('[Matomo] _paq already exists, skipping init');
      }
      return;
    }

    if (this.debugEnabled) {
      console.info('[Matomo] Initializing');
    }

    const _paq = w._paq = w._paq || [];
    _paq.push(['setTrackerUrl', this.matomoUrl + 'matomo.php']);
    _paq.push(['setSiteId', this.matomoSiteId]);
    _paq.push(['enableLinkTracking']);
    _paq.push(['trackPageView']);

    const d = document;
    const g = d.createElement('script');
    const s = d.getElementsByTagName('script')[0];
    g.async = true;
    g.src = this.matomoUrl + 'matomo.js';
    g.onload = () => {
      if (this.debugEnabled) {
        console.info('[Matomo] Script loaded');
      }
    };
    g.onerror = () => {
      if (this.debugEnabled) {
        console.error('[Matomo] Script failed to load:', g.src);
      }
    };
    s.parentNode?.insertBefore(g, s);
  }

  trackPageView(url: string): void {
    const w = window as any;
    const _paq = w._paq as any[] | undefined;
    if (!_paq || !this.hasAnalyticsConsent()) {
      if (this.debugEnabled) {
        console.info('[Matomo] trackPageView skipped', { hasPaq: !!_paq, hasConsent: this.hasAnalyticsConsent() });
      }
      return;
    }

    _paq.push(['setCustomUrl', url]);
    _paq.push(['trackPageView']);
    if (this.debugEnabled) {
      console.info('[Matomo] trackPageView', url);
    }
  }
}
